import { useEffect, useState } from "react";
import Logo from "./Logo";
import { useSettings } from "../context/SettingsContext";

const DEFAULT_LINKS = [
  { id: "n-collezione", label: "Collezione", target: "collezione", enabled: true },
  { id: "n-chi-siamo", label: "Chi siamo", target: "chi-siamo", enabled: true },
  { id: "n-contatti", label: "Contatti", target: "contatti", enabled: true },
];

export default function Header({ maintenance = false }) {
  const { settings } = useSettings();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const threshold = Math.max(window.innerHeight * 0.7, 200);
      setScrolled(window.scrollY > threshold);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (link) => {
    if (!link.target) return;
    if (/^https?:\/\//.test(link.target)) {
      window.open(link.target, "_blank");
      return;
    }
    document.getElementById(link.target)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const raw = settings.nav_links && settings.nav_links.length ? settings.nav_links : DEFAULT_LINKS;
  const links = raw.filter((l) => l.enabled !== false && l.label);

  if (maintenance) {
    return (
      <header data-testid="site-header" className="fixed top-0 inset-x-0 z-40 py-6">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          {settings.wordmark && <Logo label={settings.wordmark} dataTestid="logo-button" />}
        </div>
      </header>
    );
  }

  // Scrolled state: glass header, logo + nav on right
  if (scrolled) {
    return (
      <header
        data-testid="site-header"
        className="fixed top-0 inset-x-0 z-40 tr-glass py-3 transition-all duration-500"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 sm:px-12">
          <Logo
            label={settings.wordmark || "tramæ"}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            dataTestid="logo-button"
          />
          <nav className="hidden md:flex items-center gap-10">
            {links.map((l, i) => (
              <button
                key={l.id || i}
                data-testid={`nav-${l.target || i}`}
                onClick={() => goTo(l)}
                className="font-sub text-[0.78rem] uppercase tracking-[0.24em] hover:text-[color:var(--tr-accent)] transition-colors"
                style={{ color: "#1A1918" }}
              >
                {l.label}
              </button>
            ))}
          </nav>
        </div>
      </header>
    );
  }

  // Hero state: no logo, links distributed left → center → right
  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-40 py-6 transition-all duration-500"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 flex items-center justify-between gap-6">
        {links.length === 0 ? null : links.length === 1 ? (
          <div className="w-full flex justify-end">
            <NavBtn link={links[0]} onClick={goTo} underline />
          </div>
        ) : (
          links.map((l, i) => (
            <div
              key={l.id || i}
              className={
                i === 0
                  ? "flex-1 flex justify-start"
                  : i === links.length - 1
                  ? "flex-1 flex justify-end"
                  : "flex-1 flex justify-center"
              }
            >
              <NavBtn link={l} onClick={goTo} underline={i === links.length - 1} />
            </div>
          ))
        )}
      </div>
    </header>
  );
}

function NavBtn({ link, onClick, underline }) {
  return (
    <button
      data-testid={`nav-${link.target || link.id}`}
      onClick={() => onClick(link)}
      className={`font-sub text-[0.78rem] uppercase tracking-[0.24em] hover:text-[color:var(--tr-accent)] transition-colors ${
        underline ? "border-b border-[color:var(--tr-ink)] pb-1 hover:border-[color:var(--tr-accent)]" : ""
      }`}
      style={{ color: "#1A1918" }}
    >
      {link.label}
    </button>
  );
}
